import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { orderingFetch } from '../lib/ordering-fetch'
import type { User } from '../types/ordering'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isAdmin: boolean
  isSuperadmin: boolean
  isViewOnly: boolean
  isIntern: boolean
  isOfficeSupport: boolean
  isSalesRep: boolean
  canManageQuotes: boolean
  canManageUsers: boolean
  canAccessAdminData: boolean
  /** Can assign farmers to reps (leads/RSM/admin). Mirrors rbac.can_assign_farmers. */
  canAssignFarmers: boolean
  /** This user is a sales_rep in a dealer with per-rep farmer visibility ON. */
  isFarmerVisibilityScoped: boolean
  /** Internal YI staff (everyone except external dealer users — consultant/sales_rep). */
  isInternalStaff: boolean
  /** Can edit a product's AgVend link: admin + office_support (order processors, e.g. Alexis). */
  canManageAgvendLink: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isAdmin: false,
  isSuperadmin: false,
  isViewOnly: false,
  isIntern: false,
  isOfficeSupport: false,
  isSalesRep: false,
  canManageQuotes: false,
  canManageUsers: false,
  canAccessAdminData: false,
  canAssignFarmers: false,
  isFarmerVisibilityScoped: false,
  isInternalStaff: false,
  canManageAgvendLink: false,
  login: async () => {},
  logout: async () => {},
  refreshSession: async () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = user !== null
  const isAdmin = user?.role === 'admin'
  const isSuperadmin = user?.is_superadmin === true
  const isViewOnly = user?.role === 'view_only_admin'
  const isIntern = user?.role === 'intern'
  const isOfficeSupport = user?.role === 'office_support'
  const isSalesRep = user?.role === 'sales_rep'
  // Mirrors API can_manage_quotes (rbac.py): per-user override wins, else every
  // role except view_only_admin and intern — consultants/RMs included.
  const canManageQuotes = !!user && (user.perm_create_quotes ?? !['view_only_admin', 'intern'].includes(user.role))
  const canManageUsers = !!user && ['admin', 'office_support', 'regional_manager', 'consultant'].includes(user.role)
  const canAccessAdminData = isAdmin
  // Mirrors API rbac.can_assign_farmers — leads/RSM/admin, never a sales_rep.
  const canAssignFarmers = !!user && ['admin', 'office_support', 'regional_manager', 'consultant'].includes(user.role)
  const isFarmerVisibilityScoped = isSalesRep && user?.feature_flags?.per_rep_farmer_visibility === true
  // Internal YI staff = all non-dealer roles (external = consultant / sales_rep).
  const isInternalStaff = !!user && ['admin', 'office_support', 'regional_manager', 'view_only_admin', 'intern'].includes(user.role)
  // Editing a product's AgVend link is an order-submission concern → admin +
  // office_support (order processors like Alexis). Mirrors can_manage_agvend_link (rbac.py).
  const canManageAgvendLink = !!user && ['admin', 'office_support'].includes(user.role)

  // Fetch session on mount
  useEffect(() => {
    let cancelled = false
    async function fetchSession() {
      try {
        const res = await orderingFetch('/api/auth/session')
        if (res.ok) {
          const data = await res.json()
          if (!cancelled) setUser(data.user)
        }
      } catch {
        // Session not available — user is not logged in
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchSession()
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const res = await orderingFetch('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(err.detail || 'Login failed')
    }
    const data = await res.json()
    // Drop any prior user's cached queries before the new session's data loads,
    // so switching accounts never shows the previous user's farmers/dashboard.
    queryClient.clear()
    setUser(data.user ?? data)
  }, [queryClient])

  const logout = useCallback(async () => {
    await orderingFetch('/api/auth/logout', { method: 'POST' })
    setUser(null)
    queryClient.clear()
  }, [queryClient])

  const refreshSession = useCallback(async () => {
    const res = await orderingFetch('/api/auth/session')
    if (res.ok) {
      const data = await res.json()
      setUser(data.user)
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        isSuperadmin,
        isViewOnly,
        isIntern,
        isOfficeSupport,
        isSalesRep,
        canManageQuotes,
        canManageUsers,
        canAccessAdminData,
        canAssignFarmers,
        isFarmerVisibilityScoped,
        isInternalStaff,
        canManageAgvendLink,
        login,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
